import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Typography,
  Box,
  CardContent,
  Toolbar,
  IconButton,
  Badge,
  Divider,
  Menu,
  MenuItem,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Tooltip,
  ListItemButton,
  ListItemText,
  List,
  ListSubheader,
  Card,
  CardHeader,
  ListItem,
  CardActions,
  ListItemAvatar,
  Avatar,
  Autocomplete,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";

import { connect } from "react-redux";
import NoRecordMessage from "../../common/components/NoRecordMessage";

import {
  getProviderPrograms,
  saveTraining,
  getTraineeSubCategories,
} from "../../../../store/cpd/actions";

import { getSchoolSubLevelSubjects } from "../../../../store/posts/actions";

import ConfirmationDialog from "../../common/components/ConfirmationDialog";
import ReactPaginate from "react-paginate";

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useRequiredAuth } from "../../common/guard/requiredAuth";
import axios from "axios";
import {
  showError,
  showSuccess,
  getApiErrorMessage,
} from "../../../toastify";

import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { isEmpty } from "lodash";
import NumberFormat from "react-number-format";

import {
  serializeTrainingFormDraft,
  hydrateTrainingFormDraft,
  newTargetTraineeRowKey,
  cloneCohortWithNewTargetRowKeys,
} from "../utils/trainingDraftSnapshot";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const trainingCohortDefaults = {
  startDate: null,
  endDate: null,
  numOfTrainees: 0,
  numOfHours: 0,
  budget: 0,

  errors: {},

  venues: [
    {
      venueType: null,
      district: null,
      sectors: [],
      sector: null,
      venues: [],
      venue: null,
      numOfTrainees: 0,
      trainerSearchTerm: "",
      trainers: [],
      errors: {},
    },
  ],

  targetTrainees: [
    {
      district: null,
      sectors: [],
      sector: null,
      schools: [],
      school: null,
      errors: {},
      traineeCategories: [
        {
          category: null,
          subCategories: [],
          subCategory: null,
          subLevel: null,
          subjects: [],
          subject: null,
          numOfTrainees: "",
          errors: {},
        },
      ],
    },
  ],
};

const trainingMaterialDefaults = {
  id: null,
  description: "",
  url: "",
  errors: {},
};

/** Avoid mounting hundreds of Autocomplete trees when resuming large drafts. */
const TARGET_TRAINEE_LOCATIONS_PAGE_SIZE = 25;

function isNeedsAssessmentUploadApiMessage(text) {
  if (!text) return false;
  const s = text.toLowerCase();
  return (
    s.includes("file too large") ||
    s.includes("invalid file type") ||
    s.includes("needs assessment file")
  );
}

const TrainingFormDialog = (props) => {
  const {
    loading,
    user,
    saveTraining,
    districts,
    showTrainingFormDialog,
    setShowTrainingFormDialog,
    providerPrograms,
    getProviderPrograms,
    academicYears,
    traineeCategories,
    schoolSubLevels,
    venueTypes,
    resumeDraftId,
    resumeFormSnapshot,
    resumeNeedsAssessmentFilePath,
    onClearResumeDraft,
  } = props;

  useEffect(() => {
    getProviderPrograms(user.id);
  }, []);

  const { hasPermissions } = useRequiredAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [providerProgram, setProviderProgram] = useState(null);

  const [subPrograms, setSubPrograms] = useState([]);
  const [subProgram, setSubProgram] = useState(null);

  const [topics, setTopics] = useState([]);

  const [selectedTopics, setSelectedTopics] = useState([]);
  const [title, setTitle] = useState("");

  const [trainingCohorts, setTrainingCohorts] = useState(() => [
    cloneCohortWithNewTargetRowKeys(trainingCohortDefaults),
  ]);

  const [trainingMaterials, setTrainingMaterials] = useState([
    { ...trainingMaterialDefaults },
  ]);

  const [needsAssessment, setNeedsAssessment] = useState({
    file: null,
    description: "",
  });

  const [errors, setErrors] = useState({
    titleError: "",
    programError: "",
    subProgramError: "",
    topicsError: "",
    descriptionError: "",
    fileError: "",
    hasError: false,
  });

  const [activeDraftId, setActiveDraftId] = useState(null);
  const [persistedNeedsAssessmentPath, setPersistedNeedsAssessmentPath] =
    useState(null);
  const [draftLinkedProviderProgramId, setDraftLinkedProviderProgramId] =
    useState(null);
  const [draftLinkedSubProgramId, setDraftLinkedSubProgramId] =
    useState(null);
  const [draftLinkedTopicIds, setDraftLinkedTopicIds] = useState(null);
  const [
    targetTraineeLocationPageByCohortIdx,
    setTargetTraineeLocationPageByCohortIdx,
  ] = useState({});

  const formValidator = () => {
    const error = {
      titleError: "",
      programError: "",
      subProgramError: "",
      topicsError: "",
      descriptionError: "",
      fileError: "",
      hasError: false,
    };

    if (!title) {
      error.titleError = "Required";
      error.hasError = true;
    }

    if (!providerProgram) {
      error.programError = "Required";
      error.hasError = true;
    }

    if (!subProgram) {
      error.subProgramError = "Required";
      error.hasError = true;
    }

    if (!selectedTopics.length) {
      error.topicsError = "Required";
      error.hasError = true;
    }

    if (!needsAssessment.description) {
      error.descriptionError = "Required";
      error.hasError = true;
    }

    if (!needsAssessment.file && !persistedNeedsAssessmentPath) {
      error.fileError = "Required";
      error.hasError = true;
    }

    const tmpTrainingCohorts = [...trainingCohorts];

    tmpTrainingCohorts?.forEach((cohot) => {
      cohot.errors.startDate = !cohot.startDate ? "Required" : undefined;

      cohot.errors.endDate = !cohot.endDate ? "Required" : undefined;

      cohot.errors.numOfTrainees = !cohot.numOfTrainees
        ? "Required"
        : undefined;

      cohot.errors.numOfHours = !cohot.numOfHours ? "Required" : undefined;

      cohot.errors.budget = !cohot.budget ? "Required" : undefined;

      for (const errorKey in cohot.errors) {
        if (!!cohot.errors[errorKey]) error.hasError = true;
      }

      let totalNumOfTrainees = 0;
      cohot.targetTrainees?.forEach((targetTrainee, targetTraineeIndex) => {
        targetTrainee.errors.district = !targetTrainee.district
          ? "Required"
          : undefined;

        targetTrainee.errors.sector = !targetTrainee.sector
          ? "Required"
          : undefined;

        targetTrainee.errors.school = !targetTrainee.school
          ? "Required"
          : undefined;

        for (const errorKey in targetTrainee.errors) {
          if (!!targetTrainee.errors[errorKey]) error.hasError = true;
        }

        targetTrainee.traineeCategories?.forEach(
          (traineeCategory, traineeCategoryIndex) => {
            totalNumOfTrainees += +traineeCategory.numOfTrainees;

            traineeCategory.errors.category = !traineeCategory.category
              ? "Required"
              : undefined;

            const subCats = traineeCategory.subCategories || [];
            const subCount = subCats.length;
            if (subCount === 1 && traineeCategory.category) {
              traineeCategory.subCategory =
                traineeCategory.subCategory || subCats[0];
            }
            traineeCategory.errors.subCategory =
              subCount === 0
                ? undefined
                : subCount === 1
                  ? undefined
                  : !traineeCategory.subCategory
                    ? "Required"
                    : undefined;

            traineeCategory.errors.subLevel =
              !traineeCategory.subLevel &&
                !!traineeCategory?.category?.hasSchoolLevel
                ? "Required"
                : undefined;

            traineeCategory.errors.subject =
              !traineeCategory.subject && traineeCategory?.category?.id === 1
                ? "Required"
                : undefined;

            traineeCategory.errors.numOfTrainees =
              !+traineeCategory.numOfTrainees
                ? "Required"
                : !!+traineeCategory.numOfTrainees &&
                  +totalNumOfTrainees !== +cohot.numOfTrainees &&
                  traineeCategoryIndex + 1 ===
                  targetTrainee.traineeCategories.length &&
                  targetTraineeIndex + 1 === cohot.targetTrainees.length
                  ? "Total trainees must match the cohort total"
                  : undefined;

            for (const errorKey in traineeCategory.errors) {
              if (!!traineeCategory.errors[errorKey]) error.hasError = true;
            }
          },
        );
      });

      totalNumOfTrainees = 0;
      cohot.venues?.forEach((venue, venueIndex) => {
        totalNumOfTrainees += +venue.numOfTrainees;

        venue.errors.district = !venue.district ? "Required" : undefined;

        venue.errors.sector = !venue.sector ? "Required" : undefined;

        venue.errors.venue = !venue.venue ? "Required" : undefined;

        venue.errors.numOfTrainees = !+venue.numOfTrainees
          ? "Required"
          : !!+venue.numOfTrainees &&
            +totalNumOfTrainees !== +cohot.numOfTrainees &&
            venueIndex + 1 === cohot.venues.length
            ? "Total trainees must match the cohort total"
            : undefined;

        venue.errors.trainers = !venue.trainers?.length
          ? "Trainers are not set"
          : undefined;

        for (const errorKey in venue.errors) {
          if (!!venue.errors[errorKey]) error.hasError = true;
        }
      });
    });

    setTrainingCohorts([...tmpTrainingCohorts]);

    const tmpTrainingMaterials = [...trainingMaterials];

    tmpTrainingMaterials?.forEach((trainingMaterial) => {
      trainingMaterial.errors.description = !trainingMaterial.description
        ? "Required"
        : undefined;
      trainingMaterial.errors.url = !trainingMaterial.url
        ? "Required"
        : undefined;

      for (const errorKey in trainingMaterial.errors) {
        if (!!trainingMaterial.errors[errorKey]) error.hasError = true;
      }
    });
    setErrors(error);

    if (error.hasError) {
      const lines = [
        "Please review the form and correct the fields highlighted in red.",
      ];
      if (error.descriptionError || error.fileError) {
        lines.push(
          "Under «Needs assessment»: add a description and a PDF (maximum 5 MB).",
        );
      }
      showError(lines.join(" "));
      return { ok: false };
    }

    return { ok: true, trainingCohorts: tmpTrainingCohorts };
  };

  const onSave = () => {
    const validation = formValidator();
    if (!validation.ok) return;

    const cohortsPayload = validation.trainingCohorts;

    const data = {
        providerProgramId: providerProgram?.id,
        subProgramId: subProgram?.id,
        description: needsAssessment.description,
        title: title,
        // file: needsAssessment.file,
        topics: selectedTopics.map((topic) => topic.id),
        trainingMaterials: trainingMaterials.map((material) => ({
          description: material.description,
          url: material.url,
        })),

        cohorts: cohortsPayload.map((cohort, cohortIndex) => ({
          cohortNumber: cohortIndex + 1,
          startDate: cohort.startDate,
          endDate: cohort.endDate,
          numOfTrainees: cohort.numOfTrainees || 0,
          numOfHours: cohort.numOfHours || 0,
          budget: cohort.budget || 0,

          targetTraineesLocations: cohort.targetTrainees.map(
            (traineeLocation) => ({
              districtId: traineeLocation.district?.id || null,
              sectorId: traineeLocation.sector?.id || null,
              schoolScopeId: traineeLocation.school?.schoolScopeId || null,

              traineesCategories: traineeLocation.traineeCategories.map(
                (traineeCategory) => ({
                  traineeCategoryId: traineeCategory?.category?.id || null,
                  traineeSubCategoryId:
                    traineeCategory?.subCategory?.id || null,
                  schoolSubLevelId: traineeCategory?.subLevel?.id || null,
                  subjectId: traineeCategory?.subject?.id || null,
                  numOfTrainees: traineeCategory?.numOfTrainees || 0,
                }),
              ),
            }),
          ),
          venues: cohort.venues.map((cohortVenue) => ({
            venueId: cohortVenue.venue?.id,
            numOfTrainees: cohortVenue.numOfTrainees,

            trainers: cohortVenue.trainers.map((trainer) => ({
              trainerId: trainer.id,
              isVenueCoordinator: trainer.isVenueCoordinator || false,
            })),
          })),
        })),
    };

    if (persistedNeedsAssessmentPath && !needsAssessment.file) {
      data.existingNeedsAssessmentPath = persistedNeedsAssessmentPath;
    }

    const formData = new FormData();

    formData.append("formData", JSON.stringify(data));
    if (needsAssessment.file) {
      formData.append("files", needsAssessment.file);
    }

    saveTraining(
      formData,
      () => {
        const draftIdToRemove = activeDraftId;
        if (draftIdToRemove) {
          axios
            .delete(
              `/api/cpd/training-drafts/${draftIdToRemove}?keepUploadedFile=true`,
            )
            .catch(() => {});
        }
        onClose();
      },
      (err) => {
        const apiMsg = getApiErrorMessage(err);
        if (apiMsg && isNeedsAssessmentUploadApiMessage(apiMsg)) {
          setErrors((prev) => ({ ...prev, fileError: apiMsg }));
        }
      },
    );
  };

  const onSaveDraft = async () => {
    try {
      setIsLoading(true);
      const formSnapshot = serializeTrainingFormDraft({
        title,
        providerProgram,
        subProgram,
        selectedTopics,
        needsAssessment,
        trainingCohorts,
        trainingMaterials,
      });

      const payload = { id: activeDraftId, formSnapshot };
      const formData = new FormData();
      formData.append("formData", JSON.stringify(payload));
      if (needsAssessment.file) {
        formData.append("files", needsAssessment.file);
      }

      const { data } = await axios.post("/api/cpd/training-drafts", formData);
      setActiveDraftId(data.id);
      if (data.needsAssessmentFilePath) {
        setPersistedNeedsAssessmentPath(data.needsAssessmentFilePath);
        setNeedsAssessment((na) => ({ ...na, file: null }));
      }
      showSuccess("Draft saved");
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      showError(e);
      const apiMsg = getApiErrorMessage(e);
      if (apiMsg && isNeedsAssessmentUploadApiMessage(apiMsg)) {
        setErrors((prev) => ({ ...prev, fileError: apiMsg }));
      }
    }
  };

  const getSubPrograms = async (programId) => {
    try {
      setIsLoading(true);

      setSelectedTopics([]);
      setTopics([]);

      setSubProgram(null);
      setSubPrograms([]);

      const { data } = await axios.get(`/api/cpd/sub-programs/${programId}`);

      setSubPrograms(data);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const getTopics = async (subProgramId) => {
    try {
      setIsLoading(true);

      setSelectedTopics([]);
      setTopics([]);

      const { data } = await axios.get(`/api/cpd/topics/${subProgramId}`);

      setTopics(data);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!!providerProgram?.programId)
      getSubPrograms(providerProgram?.programId);
  }, [providerProgram]);

  useEffect(() => {
    if (!!subProgram?.id) getTopics(subProgram?.id);
  }, [subProgram]);

  useEffect(() => {
    if (!showTrainingFormDialog || !resumeFormSnapshot) {
      return;
    }
    const hydrated = hydrateTrainingFormDraft(
      resumeFormSnapshot,
      trainingCohortDefaults,
      trainingMaterialDefaults,
    );
    if (!hydrated) {
      return;
    }
    setTitle(hydrated.title);
    setProviderProgram(hydrated.providerProgram);
    setSubProgram(hydrated.subProgram);
    setSelectedTopics(hydrated.selectedTopics);
    setNeedsAssessment(hydrated.needsAssessment);
    setTrainingMaterials(hydrated.trainingMaterials);
    setTrainingCohorts(hydrated.trainingCohorts);
    setErrors({
      titleError: "",
      programError: "",
      subProgramError: "",
      topicsError: "",
      descriptionError: "",
      fileError: "",
      hasError: false,
    });
    setActiveDraftId(resumeDraftId || null);
    setPersistedNeedsAssessmentPath(resumeNeedsAssessmentFilePath || null);
    setDraftLinkedProviderProgramId(hydrated.providerProgram?.id || null);
    setDraftLinkedSubProgramId(hydrated.subProgram?.id || null);
    setDraftLinkedTopicIds(
      (hydrated.selectedTopics || []).map((t) => t.id).filter(Boolean),
    );
  }, [
    showTrainingFormDialog,
    resumeFormSnapshot,
    resumeDraftId,
    resumeNeedsAssessmentFilePath,
  ]);

  useEffect(() => {
    if (!draftLinkedProviderProgramId || !providerPrograms?.length) {
      return;
    }
    const match = providerPrograms.find(
      (p) => p.id === draftLinkedProviderProgramId,
    );
    if (match) {
      setProviderProgram(match);
      setDraftLinkedProviderProgramId(null);
    }
  }, [providerPrograms, draftLinkedProviderProgramId]);

  useEffect(() => {
    if (!draftLinkedSubProgramId || !subPrograms?.length) {
      return;
    }
    const match = subPrograms.find((s) => s.id === draftLinkedSubProgramId);
    if (match) {
      setSubProgram(match);
      setDraftLinkedSubProgramId(null);
    }
  }, [subPrograms, draftLinkedSubProgramId]);

  useEffect(() => {
    if (!draftLinkedTopicIds?.length || !topics?.length) {
      return;
    }
    const idSet = new Set(draftLinkedTopicIds);
    setSelectedTopics(topics.filter((t) => idSet.has(t.id)));
    setDraftLinkedTopicIds(null);
  }, [topics, draftLinkedTopicIds]);

  useEffect(() => {
    setTargetTraineeLocationPageByCohortIdx((prev) => {
      let next = null;
      trainingCohorts.forEach((cohort, idx) => {
        const n = cohort.targetTrainees?.length || 0;
        const maxPage = Math.max(
          0,
          Math.ceil(n / TARGET_TRAINEE_LOCATIONS_PAGE_SIZE) - 1,
        );
        const current = prev[idx] ?? 0;
        if (current > maxPage) {
          if (!next) next = { ...prev };
          next[idx] = maxPage;
        }
      });
      return next ?? prev;
    });
  }, [trainingCohorts]);

  // useEffect(() => {
  //   const cohots = [...trainingCohorts];

  //   if (!!cohots[0]) {
  //     cohots[0].academicYear =
  //       academicYears.find(({ active }) => !!active) || null;

  //     cohots[0].currency = currencies.find(({ id }) => id === "RWF") || null;
  //   }

  //   setTrainingCohorts(cohots);
  // }, [academicYears]);

  const onClose = () => {
    setTrainingCohorts([cloneCohortWithNewTargetRowKeys(trainingCohortDefaults)]);
    setTitle("");
    setProviderProgram(null);
    setSubProgram(null);
    setSubPrograms([]);
    setTopics([]);
    setSelectedTopics([]);
    setTrainingMaterials([{ ...trainingMaterialDefaults }]);
    setNeedsAssessment({ file: null, description: "" });
    setErrors({
      titleError: "",
      programError: "",
      subProgramError: "",
      topicsError: "",
      descriptionError: "",
      fileError: "",
      hasError: false,
    });
    setActiveDraftId(null);
    setPersistedNeedsAssessmentPath(null);
    setDraftLinkedProviderProgramId(null);
    setDraftLinkedSubProgramId(null);
    setDraftLinkedTopicIds(null);
    setTargetTraineeLocationPageByCohortIdx({});
    onClearResumeDraft?.();
    setShowTrainingFormDialog(false);
  };

  const getApiData = async (url, param, cb = null) => {
    try {
      setIsLoading(true);

      !!cb && cb([]);

      if (!!param) {
        const { data } = await axios.get(`${url.replace(/\/$/, "")}/${param}`);

        !!cb && cb(data);
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const searchTrainer = async (trainerSearchTerm) => {
    try {
      setIsLoading(true);

      const { data } = await axios.post(`/api/cpd/search-trainer`, {
        trainerSearchTerm,
      });

      setIsLoading(false);

      return data;
    } catch (error) {
      setIsLoading(false);
      showError(error);
    }
  };

  return (
    <Dialog
      onClose={onClose}
      open={showTrainingFormDialog}
      fullWidth
      maxWidth="xl"
    >
      <DialogTitle className="text-primary">
        {/* <Typography> */}
        <span className="text-primary text-uppercase">
          New Training Application
        </span>
        {/* </Typography> */}

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <span className="material-icons">close</span>
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div
          style={{
            minHeight: "69vh",
          }}
        >
          <div className="row">
            <div className="col-12">
              <fieldset
                className={`px-3 no-gutters mt-3 form-group border bg-white rounded elevated`}
              >
                <legend
                  className={` w-auto text-primary`}
                  style={{
                    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                    fontWeight: 400,
                    fontSize: "0.82rem",
                    lineHeight: "1em",
                    letterSpacing: "0.00938em",
                  }}
                >
                  Training application
                </legend>

                <div className="row">
                  <div className="col-12 mt-2">
                    <TextField
                      error={!!errors.titleError}
                      helperText={errors.titleError || ""}
                      required
                      autoFocus
                      size="small"
                      fullWidth
                      name="title"
                      label="Training title"
                      placeholder="Training title"
                      value={title}
                      onChange={(e) => {
                        setTitle(e.target.value || "");

                        setErrors({ ...errors, titleError: "" });
                      }}
                    />
                  </div>
                  <div className="col-4">
                    <Autocomplete
                      className="mt-3"
                      size="small"
                      defaultValue={null}
                      value={providerProgram}
                      options={providerPrograms}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      getOptionLabel={(option) => option.program.name}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          {option.program.name}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!errors.programError}
                          helperText={errors.programError || ""}
                          name="providerProgram"
                          label="CPD area"
                          placeholder="CPD area"
                          inputProps={{
                            ...params.inputProps,
                          }}
                        />
                      )}
                      onChange={(event, providerProgram) => {
                        setProviderProgram(providerProgram || null);

                        setErrors({ ...errors, programError: "" });
                      }}
                    />
                  </div>
                  <div className="col-4">
                    <Autocomplete
                      className="mt-3"
                      size="small"
                      defaultValue={null}
                      value={subProgram}
                      options={subPrograms}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      getOptionLabel={(option) => option.name}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          {option.name}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!errors.subProgramError}
                          helperText={errors.subProgramError || ""}
                          name="subProgram"
                          label="Course"
                          placeholder="Course"
                          inputProps={{
                            ...params.inputProps,
                          }}
                        />
                      )}
                      onChange={(event, subProgram) => {
                        setSubProgram(subProgram || null);

                        setErrors({ ...errors, subProgramError: "" });
                      }}
                    />
                  </div>

                  <div className="col-4">
                    <Autocomplete
                      multiple
                      className="mt-3"
                      size="small"
                      // disableCloseOnSelect
                      defaultValue={[]}
                      value={selectedTopics}
                      options={topics}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      getOptionLabel={(option) => option.name}
                      renderOption={(props, option, { selected }) => (
                        <ListItem dense {...props}>
                          <ListItemAvatar>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                          </ListItemAvatar>
                          <ListItemText primary={option.name} />
                        </ListItem>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!errors.topicsError}
                          helperText={errors.topicsError || ""}
                          name="topics"
                          label="Modules"
                          placeholder="Modules"
                          inputProps={{
                            ...params.inputProps,
                          }}
                        />
                      )}
                      onChange={(event, selected) => {
                        setSelectedTopics(selected || []);

                        setErrors({ ...errors, topicsError: "" });
                      }}
                    />
                  </div>
                </div>

                <fieldset
                  className={`px-3 mb-3 no-gutters form-group border bg-light rounded elevated`}
                >
                  <legend
                    className={` w-auto text-primary`}
                    style={{
                      fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                      fontWeight: 400,
                      fontSize: "0.82rem",
                      lineHeight: "1em",
                      letterSpacing: "0.00938em",
                    }}
                  >
                    [{trainingCohorts.length}] Cohort
                    {trainingCohorts.length > 1 ? "s" : ""}
                    <IconButton
                      color="primary"
                      onClick={() => {
                        const cohots = [...trainingCohorts];

                        cohots.push(
                          cloneCohortWithNewTargetRowKeys(trainingCohortDefaults),
                        );

                        setTrainingCohorts(cohots);
                      }}
                    >
                      <Tooltip title="Add cohot" placement="top">
                        <span className="material-icons">add</span>
                      </Tooltip>
                    </IconButton>
                  </legend>

                  {trainingCohorts.map((cohort, cohortIndex) => (
                    <fieldset
                      key={cohortIndex}
                      className={`row mx-0 border rounded elevated mb-3`}
                    >
                      <legend
                        className={`px-3 w-auto text-dark`}
                        style={{
                          fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                          fontWeight: 400,
                          fontSize: "0.8rem",
                          lineHeight: "1em",
                          letterSpacing: "0.00938em",
                        }}
                      >
                        Cohort #{cohortIndex + 1}
                      </legend>
                      <div className="col-12 mt-n2">
                        <IconButton
                          disabled={trainingCohorts.length <= 1}
                          style={{ top: 0 }}
                          className="float-right mt-n2 mr-n3"
                          color="error"
                          onClick={() => {
                            const cohots = [...trainingCohorts];

                            cohots.splice(cohortIndex, 1);

                            setTrainingCohorts(cohots);
                            setTargetTraineeLocationPageByCohortIdx((prev) => {
                              const next = {};
                              for (let newIdx = 0; newIdx < cohots.length; newIdx++) {
                                const oldIdx =
                                  newIdx < cohortIndex ? newIdx : newIdx + 1;
                                if (prev[oldIdx] != null) {
                                  next[newIdx] = prev[oldIdx];
                                }
                              }
                              return next;
                            });
                          }}
                        >
                          <Tooltip title="Remove cohot" placement="top">
                            <span className="material-icons">
                              remove_circle
                            </span>
                          </Tooltip>
                        </IconButton>
                      </div>

                      <div className="col-3">
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DesktopDatePicker
                            minDate={new Date()}
                            label="Start date"
                            inputFormat="dd/MM/yyyy"
                            value={cohort.startDate || null}
                            onChange={(date) => {
                              const cohots = [...trainingCohorts];

                              cohots[cohortIndex] = {
                                ...cohort,
                                startDate: date,
                                errors: {
                                  ...cohort.errors,
                                  startDate: undefined,
                                },
                              };

                              setTrainingCohorts(cohots);
                            }}
                            renderInput={(params) => (
                              <TextField
                                name="startDate"
                                onKeyDown={(e) => e.preventDefault()}
                                size="small"
                                fullWidth
                                {...params}
                                error={!!cohort.errors?.startDate}
                                helperText={cohort.errors?.startDate || ""}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </div>
                      <div className="col-3">
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DesktopDatePicker
                            disabled={!cohort.startDate}
                            minDate={cohort.startDate || new Date()}
                            label="End date"
                            inputFormat="dd/MM/yyyy"
                            value={cohort.endDate || null}
                            onChange={(date) => {
                              const cohots = [...trainingCohorts];

                              cohots[cohortIndex] = {
                                ...cohort,
                                endDate: date,
                                errors: {
                                  ...cohort.errors,
                                  endDate: undefined,
                                },
                              };

                              setTrainingCohorts(cohots);
                            }}
                            renderInput={(params) => (
                              <TextField
                                name="endDate"
                                onKeyDown={(e) => e.preventDefault()}
                                size="small"
                                fullWidth
                                {...params}
                                error={!!cohort.errors?.endDate}
                                helperText={cohort.errors?.endDate || ""}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </div>
                      <div className="col-2">
                        <TextField
                          error={!!cohort.errors?.numOfHours}
                          helperText={cohort.errors?.numOfHours || ""}
                          size="small"
                          className="mb-3"
                          required
                          type="number"
                          fullWidth
                          name="numOfHours"
                          label="Total number of hours"
                          placeholder="Total number of hours"
                          variant="outlined"
                          value={cohort.numOfHours || 0}
                          onChange={(e) => {
                            const cohots = [...trainingCohorts];

                            let value = e.target.value || "";

                            if (value.length > 1) {
                              value = value.replace(/^0+/, "");
                            }

                            cohots[cohortIndex] = {
                              ...cohort,
                              numOfHours: value,

                              errors: {
                                ...cohort.errors,
                                numOfHours: undefined,
                              },
                            };

                            setTrainingCohorts(cohots);
                          }}
                        />
                      </div>
                      <div className="col-2">
                        <TextField
                          error={!!cohort.errors?.numOfTrainees}
                          helperText={cohort.errors?.numOfTrainees || ""}
                          size="small"
                          className="mb-3"
                          required
                          type="number"
                          fullWidth
                          name="numberOfTrainees"
                          label="Number of trainees"
                          placeholder="Number of trainees"
                          variant="outlined"
                          value={cohort.numOfTrainees || 0}
                          onChange={(e) => {
                            const cohots = [...trainingCohorts];

                            let value = e.target.value || "";

                            if (value.length > 1) {
                              value = value.replace(/^0+/, "");
                            }

                            cohots[cohortIndex] = {
                              ...cohort,
                              numOfTrainees: value,
                              errors: {
                                ...cohort.errors,
                                numOfTrainees: undefined,
                              },
                            };

                            setTrainingCohorts(cohots);
                          }}
                        />
                      </div>

                      <div className="col-2">
                        <NumberFormat
                          error={!!cohort.errors?.budget}
                          helperText={cohort.errors?.budget || ""}
                          className="mb-3"
                          fullWidth
                          size="small"
                          name="budget"
                          variant="outlined"
                          label="Budget (RWF)"
                          placeholder="RWF 0"
                          prefix={"RWF "}
                          customInput={TextField}
                          value={cohort.budget}
                          thousandSeparator={true}
                          onBlur={() => {
                            if (cohort.budget === "") {
                              const cohots = [...trainingCohorts];

                              cohots[cohortIndex] = {
                                ...cohort,
                                budget: 0,
                              };

                              setTrainingCohorts(cohots);
                            }
                          }}
                          onKeyDown={() => {
                            if (+cohort.budget === 0) {
                              const cohots = [...trainingCohorts];

                              cohots[cohortIndex] = {
                                ...cohort,
                                budget: "",
                              };

                              setTrainingCohorts(cohots);
                            }
                          }}
                          onChange={(e) => {
                            const cohots = [...trainingCohorts];

                            let value = e.target.value.split("RWF ") || [];

                            value = !!value[1] ? value[1].replace(/,/g, "") : 0;

                            console.log(value);

                            cohots[cohortIndex] = {
                              ...cohort,
                              budget: value,

                              errors: { ...cohort.errors, budget: undefined },
                            };

                            setTrainingCohorts(cohots);
                          }}
                        />
                      </div>

                      <div className="col-12 px-3">
                        <fieldset
                          className={`px-3 pb-2 no-gutters form-group border  bg-light elevated rounded`}
                        >
                          <legend
                            className={` w-auto text-primary`}
                            style={{
                              fontFamily:
                                '"Roboto","Helvetica","Arial",sans-serif',
                              fontWeight: 400,
                              fontSize: "0.82rem",
                              lineHeight: "1em",
                              letterSpacing: "0.00938em",
                            }}
                          >
                            [{cohort.targetTrainees.length}] Target trainees
                            location
                            {cohort.targetTrainees.length > 1 ? "s" : ""}
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                const cohots = [...trainingCohorts];

                                const targetTrainees = [
                                  ...cohots[cohortIndex].targetTrainees,
                                ];

                                const newLocation = JSON.parse(
                                  JSON.stringify(
                                    trainingCohortDefaults.targetTrainees[0],
                                  ),
                                );
                                newLocation.rowKey = newTargetTraineeRowKey();
                                targetTrainees.unshift(newLocation);

                                cohots[cohortIndex] = {
                                  ...cohort,
                                  targetTrainees: targetTrainees,
                                };

                                setTrainingCohorts(cohots);
                              }}
                            >
                              <Tooltip title="Add cohot" placement="top">
                                <span className="material-icons">add</span>
                              </Tooltip>
                            </IconButton>
                          </legend>

                          {(() => {
                            const ttTotal = cohort.targetTrainees.length;
                            const ttPage =
                              targetTraineeLocationPageByCohortIdx[
                                cohortIndex
                              ] ?? 0;
                            const ttPageCount = Math.max(
                              1,
                              Math.ceil(
                                ttTotal / TARGET_TRAINEE_LOCATIONS_PAGE_SIZE,
                              ),
                            );
                            const ttSliceStart =
                              ttPage * TARGET_TRAINEE_LOCATIONS_PAGE_SIZE;
                            const pagedTargetTrainees =
                              cohort.targetTrainees.slice(
                                ttSliceStart,
                                ttSliceStart +
                                  TARGET_TRAINEE_LOCATIONS_PAGE_SIZE,
                              );
                            return (
                              <>
                                {pagedTargetTrainees.map(
                                  (traineeTarget, localIndex) => {
                                    const traineeTargetIndex =
                                      ttSliceStart + localIndex;
                                    return (
                              <fieldset
                                key={
                                  traineeTarget.rowKey ??
                                  `tt-fallback-${cohortIndex}-${traineeTargetIndex}`
                                }
                                className={`row border  bg-light mx-0 mb-2`}
                              >
                                <legend
                                  className={`col-12 px-3 w-auto text-dark`}
                                  style={{
                                    fontFamily:
                                      '"Roboto","Helvetica","Arial",sans-serif',
                                    fontWeight: 400,
                                    fontSize: "0.8rem",
                                    lineHeight: "1em",
                                    letterSpacing: "0.00938em",
                                  }}
                                >
                                  Location #{traineeTargetIndex + 1}
                                </legend>

                                <div className="col-12 ">
                                  <IconButton
                                    disabled={cohort.targetTrainees.length <= 1}
                                    className="float-right mt-n3 mr-n2"
                                    color="error"
                                    onClick={() => {
                                      const cohots = [...trainingCohorts];

                                      const targetTrainees = [
                                        ...cohots[cohortIndex].targetTrainees,
                                      ];

                                      targetTrainees.splice(
                                        traineeTargetIndex,
                                        1,
                                      );

                                      cohots[cohortIndex] = {
                                        ...cohort,
                                        targetTrainees: targetTrainees,
                                      };

                                      setTrainingCohorts(cohots);
                                    }}
                                  >
                                    <Tooltip
                                      title="Remove venue"
                                      placement="top"
                                    >
                                      <span className="material-icons">
                                        remove_circle_outline
                                      </span>
                                    </Tooltip>
                                  </IconButton>
                                </div>
                                <div className="col">
                                  <Autocomplete
                                    size="small"
                                    defaultValue={null}
                                    value={traineeTarget.district}
                                    options={districts}
                                    isOptionEqualToValue={(option, value) =>
                                      option.id === value.id
                                    }
                                    getOptionLabel={(option) => option.name}
                                    renderOption={(props, option) => (
                                      <Box component="li" {...props}>
                                        {option.name}
                                      </Box>
                                    )}
                                    renderInput={(params) => (
                                      <TextField
                                        variant="outlined"
                                        {...params}
                                        error={!!traineeTarget.errors?.district}
                                        helperText={
                                          traineeTarget.errors?.district || ""
                                        }
                                        name="district"
                                        label="District"
                                        placeholder="District"
                                        inputProps={{
                                          ...params.inputProps,
                                        }}
                                      />
                                    )}
                                    onChange={(event, district) => {
                                      const cohots = [...trainingCohorts];

                                      const targetTrainees = [
                                        ...cohots[cohortIndex].targetTrainees,
                                      ];

                                      getApiData(
                                        "/api/posts/sectors",
                                        district?.id,
                                        (data) => {
                                          targetTrainees[traineeTargetIndex] = {
                                            ...traineeTarget,
                                            district: district,
                                            sectors: data,
                                            sector: null,
                                            schools: [],
                                            school: null,
                                            traineeCategories: [
                                              ...trainingCohortDefaults
                                                .targetTrainees[0]
                                                .traineeCategories,
                                            ],

                                            errors: {
                                              ...traineeTarget.errors,
                                              district: undefined,
                                            },
                                          };

                                          cohots[cohortIndex] = {
                                            ...cohort,
                                            targetTrainees: targetTrainees,
                                          };

                                          setTrainingCohorts(cohots);
                                        },
                                      );
                                    }}
                                  />
                                </div>
                                <div className="col">
                                  <Autocomplete
                                    size="small"
                                    defaultValue={null}
                                    value={traineeTarget.sector}
                                    options={[
                                      { id: null, name: "All" },
                                      ...traineeTarget.sectors,
                                    ]}
                                    isOptionEqualToValue={(option, value) =>
                                      option.id === value.id
                                    }
                                    getOptionLabel={(option) => option.name}
                                    renderOption={(props, option) => (
                                      <Box component="li" {...props}>
                                        {option.name}
                                      </Box>
                                    )}
                                    renderInput={(params) => (
                                      <TextField
                                        variant="outlined"
                                        {...params}
                                        error={!!traineeTarget.errors?.sector}
                                        helperText={
                                          traineeTarget.errors?.sector || ""
                                        }
                                        name="sector"
                                        label="Sector"
                                        placeholder="Sector"
                                        inputProps={{
                                          ...params.inputProps,
                                        }}
                                      />
                                    )}
                                    onChange={(event, sector) => {
                                      const cohots = [...trainingCohorts];

                                      const targetTrainees = [
                                        ...cohots[cohortIndex].targetTrainees,
                                      ];

                                      getApiData(
                                        "/api/posts/schools",
                                        sector?.id,
                                        (data) => {
                                          targetTrainees[traineeTargetIndex] = {
                                            ...traineeTarget,
                                            sector: sector,
                                            schools: data,
                                            school:
                                              !!sector && !sector.id
                                                ? { id: null, name: "All" }
                                                : null,
                                            traineeCategories: [
                                              ...trainingCohortDefaults
                                                .targetTrainees[0]
                                                .traineeCategories,
                                            ],

                                            errors: {
                                              ...traineeTarget.errors,
                                              sector: undefined,
                                              school:
                                                !!sector && !sector.id
                                                  ? undefined
                                                  : traineeTarget?.errors
                                                    ?.school,
                                            },
                                          };

                                          cohots[cohortIndex] = {
                                            ...cohort,
                                            targetTrainees: targetTrainees,
                                          };

                                          setTrainingCohorts(cohots);
                                        },
                                      );
                                    }}
                                  />
                                </div>
                                <div className="col">
                                  <Autocomplete
                                    size="small"
                                    defaultValue={null}
                                    value={traineeTarget.school}
                                    options={[
                                      { id: null, name: "All" },
                                      ...traineeTarget.schools,
                                    ]}
                                    isOptionEqualToValue={(option, value) =>
                                      option.id === value.id
                                    }
                                    getOptionLabel={(option) => option.name}
                                    renderOption={(props, option) => (
                                      <Box component="li" {...props}>
                                        {option.name}
                                      </Box>
                                    )}
                                    renderInput={(params) => (
                                      <TextField
                                        variant="outlined"
                                        {...params}
                                        error={!!traineeTarget.errors?.school}
                                        helperText={
                                          traineeTarget.errors?.school || ""
                                        }
                                        name="school"
                                        label="School"
                                        placeholder="School"
                                        inputProps={{
                                          ...params.inputProps,
                                        }}
                                      />
                                    )}
                                    onChange={(event, school) => {
                                      const cohots = [...trainingCohorts];

                                      const targetTrainees = [
                                        ...cohots[cohortIndex].targetTrainees,
                                      ];

                                      targetTrainees[traineeTargetIndex] = {
                                        ...traineeTarget,
                                        school: school,
                                        traineeCategories: [
                                          ...trainingCohortDefaults
                                            .targetTrainees[0]
                                            .traineeCategories,
                                        ],

                                        errors: {
                                          ...traineeTarget.errors,
                                          school: undefined,
                                        },
                                      };

                                      cohots[cohortIndex] = {
                                        ...cohort,
                                        targetTrainees: targetTrainees,
                                      };

                                      setTrainingCohorts(cohots);
                                    }}
                                  />
                                </div>

                                <div className="col-12 ">
                                  <fieldset
                                    className={`px-4  form-group border  bg-white `}
                                  >
                                    <legend
                                      className={` w-auto text-primary`}
                                      style={{
                                        fontFamily:
                                          '"Roboto","Helvetica","Arial",sans-serif',
                                        fontWeight: 400,
                                        fontSize: "0.82rem",
                                        lineHeight: "1em",
                                        letterSpacing: "0.00938em",
                                      }}
                                    >
                                      [{traineeTarget.traineeCategories.length}]
                                      Trainees categor
                                      {traineeTarget.traineeCategories.length >
                                        1
                                        ? "ies"
                                        : "y"}
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => {
                                          const cohots = [...trainingCohorts];

                                          const targetTrainees = [
                                            ...cohots[cohortIndex]
                                              .targetTrainees,
                                          ];

                                          const tmpTraineeCategories = [
                                            ...traineeTarget.traineeCategories,
                                          ];

                                          tmpTraineeCategories.push({
                                            ...trainingCohortDefaults
                                              .targetTrainees[0]
                                              .traineeCategories[0],
                                          });

                                          targetTrainees[traineeTargetIndex] = {
                                            ...traineeTarget,
                                            traineeCategories:
                                              tmpTraineeCategories,
                                          };

                                          cohots[cohortIndex] = {
                                            ...cohort,
                                            targetTrainees: targetTrainees,
                                          };

                                          setTrainingCohorts(cohots);
                                        }}
                                      >
                                        <Tooltip
                                          title="Add cohot"
                                          placement="top"
                                        >
                                          <span className="material-icons">
                                            add
                                          </span>
                                        </Tooltip>
                                      </IconButton>
                                    </legend>

                                    {traineeTarget.traineeCategories.map(
                                      (
                                        traineeCategory,
                                        traineeCategoryIndex,
                                      ) => (
                                        <fieldset
                                          key={traineeCategoryIndex}
                                          className={`row d-flex align-items-center mb-2 pb-2 bg-light border`}
                                        >
                                          <legend
                                            className={`col-12 w-auto text-dark `}
                                            style={{
                                              fontFamily:
                                                '"Roboto","Helvetica","Arial",sans-serif',
                                              fontWeight: 400,
                                              fontSize: "0.8rem",
                                              lineHeight: "1em",
                                              letterSpacing: "0.00938em",
                                            }}
                                          >
                                            Category #{traineeCategoryIndex + 1}
                                          </legend>

                                          <div className="col">
                                            <Autocomplete
                                              size="small"
                                              defaultValue={null}
                                              value={traineeCategory.category}
                                              options={traineeCategories}
                                              isOptionEqualToValue={(
                                                option,
                                                value,
                                              ) => option.id === value.id}
                                              getOptionLabel={(option) =>
                                                option.name
                                              }
                                              renderOption={(props, option) => (
                                                <Box component="li" {...props}>
                                                  {option.name}
                                                </Box>
                                              )}
                                              renderInput={(params) => (
                                                <TextField
                                                  variant="standard"
                                                  {...params}
                                                  error={
                                                    !!traineeCategory.errors
                                                      ?.category
                                                  }
                                                  helperText={
                                                    traineeCategory.errors
                                                      ?.category || ""
                                                  }
                                                  name="traineeCategory"
                                                  label="Trainee Category"
                                                  placeholder="Trainee Category"
                                                  inputProps={{
                                                    ...params.inputProps,
                                                  }}
                                                />
                                              )}
                                              onChange={(event, category) => {
                                                const cohots = [
                                                  ...trainingCohorts,
                                                ];

                                                const targetTrainees = [
                                                  ...cohots[cohortIndex]
                                                    .targetTrainees,
                                                ];

                                                const tmpTraineeCategories = [
                                                  ...traineeTarget.traineeCategories,
                                                ];

                                                getApiData(
                                                  "/api/cpd/trainee-sub-categories",
                                                  category?.id,
                                                  (data) => {
                                                    tmpTraineeCategories[
                                                      traineeCategoryIndex
                                                    ] = {
                                                      ...traineeCategory,
                                                      category:
                                                        category || null,
                                                      subCategories: data,
                                                      subCategory:
                                                        data.length === 1
                                                          ? data[0]
                                                          : null,
                                                      subLevel: null,
                                                      subject: null,
                                                      subjects: [],
                                                      numOfTrainees: "",

                                                      errors: {
                                                        ...traineeCategory.errors,
                                                        category: undefined,
                                                      },
                                                    };

                                                    targetTrainees[
                                                      traineeTargetIndex
                                                    ] = {
                                                      ...traineeTarget,
                                                      traineeCategories:
                                                        tmpTraineeCategories,
                                                    };

                                                    cohots[cohortIndex] = {
                                                      ...cohort,
                                                      targetTrainees:
                                                        targetTrainees,
                                                    };

                                                    setTrainingCohorts(cohots);
                                                  },
                                                );
                                              }}
                                            />
                                          </div>
                                          {(traineeCategory.subCategories || [])
                                            .length >= 1 && (
                                              <div className="col">
                                                <Autocomplete
                                                  disabled={
                                                    !traineeCategory.category ||
                                                    (traineeCategory.subCategories || [])
                                                      .length === 1
                                                  }
                                                  size="small"
                                                  defaultValue={null}
                                                  value={
                                                    (traineeCategory.subCategories || [])
                                                      .length === 1
                                                      ? traineeCategory.subCategory ||
                                                        (traineeCategory.subCategories ||
                                                          [])[0]
                                                      : traineeCategory.subCategory
                                                  }
                                                  options={
                                                    traineeCategory.subCategories ||
                                                    []
                                                  }
                                                  isOptionEqualToValue={(
                                                    option,
                                                    value,
                                                  ) => option.id === value.id}
                                                  getOptionLabel={(option) =>
                                                    option.name
                                                  }
                                                  renderOption={(
                                                    props,
                                                    option,
                                                  ) => (
                                                    <Box
                                                      component="li"
                                                      {...props}
                                                    >
                                                      {option.name}
                                                    </Box>
                                                  )}
                                                  renderInput={(params) => (
                                                    <TextField
                                                      variant="standard"
                                                      {...params}
                                                      error={
                                                        !!traineeCategory.errors
                                                          ?.subCategory
                                                      }
                                                      helperText={
                                                        traineeCategory.errors
                                                          ?.subCategory || ""
                                                      }
                                                      name="subCategory"
                                                      label="Sub Category"
                                                      placeholder="Sub Category"
                                                      inputProps={{
                                                        ...params.inputProps,
                                                      }}
                                                    />
                                                  )}
                                                  onChange={(
                                                    event,
                                                    subCategory,
                                                  ) => {
                                                    const cohots = [
                                                      ...trainingCohorts,
                                                    ];

                                                    const targetTrainees = [
                                                      ...cohots[cohortIndex]
                                                        .targetTrainees,
                                                    ];

                                                    const tmpTraineeCategories = [
                                                      ...traineeTarget.traineeCategories,
                                                    ];

                                                    tmpTraineeCategories[
                                                      traineeCategoryIndex
                                                    ] = {
                                                      ...traineeCategory,
                                                      subCategory:
                                                        subCategory || null,
                                                      subLevel: null,
                                                      subject: null,
                                                      subjects: [],
                                                      numOfTrainees: "",

                                                      errors: {
                                                        ...traineeCategory.errors,
                                                        subCategory: undefined,
                                                      },
                                                    };

                                                    targetTrainees[
                                                      traineeTargetIndex
                                                    ] = {
                                                      ...traineeTarget,
                                                      traineeCategories:
                                                        tmpTraineeCategories,
                                                    };

                                                    cohots[cohortIndex] = {
                                                      ...cohort,
                                                      targetTrainees:
                                                        targetTrainees,
                                                    };

                                                    setTrainingCohorts(cohots);
                                                  }}
                                                />
                                              </div>
                                            )}

                                          {(!traineeCategory?.category ||
                                            !!traineeCategory?.category
                                              ?.hasSchoolLevel) && (
                                              <div className="col">
                                                <Autocomplete
                                                  disabled={
                                                    !traineeCategory.subCategory
                                                  }
                                                  size="small"
                                                  defaultValue={null}
                                                  value={traineeCategory.subLevel}
                                                  options={schoolSubLevels}
                                                  isOptionEqualToValue={(
                                                    option,
                                                    value,
                                                  ) => option.id === value.id}
                                                  getOptionLabel={(option) =>
                                                    option.name
                                                  }
                                                  renderOption={(
                                                    props,
                                                    option,
                                                  ) => (
                                                    <Box
                                                      component="li"
                                                      {...props}
                                                    >
                                                      {option.name}
                                                    </Box>
                                                  )}
                                                  renderInput={(params) => (
                                                    <TextField
                                                      variant="standard"
                                                      {...params}
                                                      error={
                                                        !!traineeCategory.errors
                                                          ?.subLevel
                                                      }
                                                      helperText={
                                                        traineeCategory.errors
                                                          ?.subLevel || ""
                                                      }
                                                      name="schoolSubLevel"
                                                      label="School Level"
                                                      placeholder="School Level"
                                                      inputProps={{
                                                        ...params.inputProps,
                                                      }}
                                                    />
                                                  )}
                                                  onChange={(event, subLevel) => {
                                                    const cohots = [
                                                      ...trainingCohorts,
                                                    ];

                                                    const targetTrainees = [
                                                      ...cohots[cohortIndex]
                                                        .targetTrainees,
                                                    ];

                                                    const tmpTraineeCategories = [
                                                      ...traineeTarget.traineeCategories,
                                                    ];

                                                    getApiData(
                                                      "/api/posts/school-sub-level-subjects",
                                                      subLevel?.id,
                                                      (data) => {
                                                        tmpTraineeCategories[
                                                          traineeCategoryIndex
                                                        ] = {
                                                          ...traineeCategory,
                                                          subLevel:
                                                            subLevel || null,
                                                          subjects: data,
                                                          subject:
                                                            subLevel?.id === 1
                                                              ? {
                                                                id: null,
                                                                name: "All",
                                                              }
                                                              : null,
                                                          numOfTrainees: "",

                                                          errors: {
                                                            ...traineeCategory.errors,
                                                            subLevel: undefined,
                                                          },
                                                        };

                                                        targetTrainees[
                                                          traineeTargetIndex
                                                        ] = {
                                                          ...traineeTarget,
                                                          traineeCategories:
                                                            tmpTraineeCategories,
                                                        };

                                                        cohots[cohortIndex] = {
                                                          ...cohort,
                                                          targetTrainees:
                                                            targetTrainees,
                                                        };

                                                        setTrainingCohorts(
                                                          cohots,
                                                        );
                                                      },
                                                    );
                                                  }}
                                                />
                                              </div>
                                            )}

                                          {(!traineeCategory?.category ||
                                            (traineeCategory?.category?.id ===
                                              1 &&
                                              traineeCategory?.subLevel?.id !==
                                              1)) && (
                                              <div className="col">
                                                <Autocomplete
                                                  disabled={
                                                    !traineeCategory.subLevel
                                                  }
                                                  size="small"
                                                  defaultValue={null}
                                                  value={traineeCategory.subject}
                                                  options={[
                                                    { id: null, name: "All" },
                                                    ...traineeCategory.subjects,
                                                  ]}
                                                  isOptionEqualToValue={(
                                                    option,
                                                    value,
                                                  ) => option.id === value.id}
                                                  getOptionLabel={(option) =>
                                                    option.name
                                                  }
                                                  renderOption={(
                                                    props,
                                                    option,
                                                  ) => (
                                                    <Box
                                                      component="li"
                                                      {...props}
                                                    >
                                                      {option.name}
                                                    </Box>
                                                  )}
                                                  renderInput={(params) => (
                                                    <TextField
                                                      variant="standard"
                                                      {...params}
                                                      error={
                                                        !!traineeCategory.errors
                                                          ?.subject
                                                      }
                                                      helperText={
                                                        traineeCategory.errors
                                                          ?.subject || ""
                                                      }
                                                      name="subject"
                                                      label="Subject"
                                                      placeholder="Subject"
                                                      inputProps={{
                                                        ...params.inputProps,
                                                      }}
                                                    />
                                                  )}
                                                  onChange={(event, subject) => {
                                                    const cohots = [
                                                      ...trainingCohorts,
                                                    ];

                                                    const targetTrainees = [
                                                      ...cohots[cohortIndex]
                                                        .targetTrainees,
                                                    ];

                                                    const tmpTraineeCategories = [
                                                      ...traineeTarget.traineeCategories,
                                                    ];

                                                    tmpTraineeCategories[
                                                      traineeCategoryIndex
                                                    ] = {
                                                      ...traineeCategory,
                                                      subject: subject || null,
                                                      numOfTrainees: "",

                                                      errors: {
                                                        ...traineeCategory.errors,
                                                        subject: undefined,
                                                      },
                                                    };

                                                    targetTrainees[
                                                      traineeTargetIndex
                                                    ] = {
                                                      ...traineeTarget,
                                                      traineeCategories:
                                                        tmpTraineeCategories,
                                                    };

                                                    cohots[cohortIndex] = {
                                                      ...cohort,
                                                      targetTrainees:
                                                        targetTrainees,
                                                    };

                                                    setTrainingCohorts(cohots);
                                                  }}
                                                />
                                              </div>
                                            )}

                                          <div className="col-3">
                                            <TextField
                                              error={
                                                !!traineeCategory.errors
                                                  ?.numOfTrainees
                                              }
                                              helperText={
                                                traineeCategory.errors
                                                  ?.numOfTrainees || ""
                                              }
                                              disabled={
                                                !traineeCategory?.category ||
                                                (!!traineeCategory?.category
                                                  ?.hasSchoolLevel &&
                                                  !traineeCategory?.subLevel) ||
                                                (traineeCategory?.category
                                                  ?.id === 1 &&
                                                  !traineeCategory?.subject) ||
                                                ((traineeCategory.subCategories || [])
                                                  .length > 1 &&
                                                  !traineeCategory?.subCategory)
                                              }
                                              variant="standard"
                                              size="small"
                                              required
                                              type="number"
                                              fullWidth
                                              name="numberOfTrainees"
                                              label="Number of trainees"
                                              placeholder="Number of trainees"
                                              value={
                                                traineeCategory.numOfTrainees ||
                                                0
                                              }
                                              onChange={(e) => {
                                                const cohots = [
                                                  ...trainingCohorts,
                                                ];

                                                const targetTrainees = [
                                                  ...cohots[cohortIndex]
                                                    .targetTrainees,
                                                ];

                                                const tmpTraineeCategories = [
                                                  ...traineeTarget.traineeCategories,
                                                ];

                                                let value =
                                                  e.target.value || "";

                                                if (value.length > 1) {
                                                  value = value.replace(
                                                    /^0+/,
                                                    "",
                                                  );
                                                }

                                                tmpTraineeCategories[
                                                  traineeCategoryIndex
                                                ] = {
                                                  ...traineeCategory,
                                                  numOfTrainees: value,

                                                  errors: {
                                                    ...traineeCategory.errors,
                                                    numOfTrainees: undefined,
                                                  },
                                                };

                                                targetTrainees[
                                                  traineeTargetIndex
                                                ] = {
                                                  ...traineeTarget,
                                                  traineeCategories:
                                                    tmpTraineeCategories,
                                                };

                                                cohots[cohortIndex] = {
                                                  ...cohort,
                                                  targetTrainees:
                                                    targetTrainees,
                                                };

                                                setTrainingCohorts(cohots);
                                              }}
                                            />
                                          </div>
                                          <div className="col-1 text-right">
                                            <IconButton
                                              disabled={
                                                traineeTarget.traineeCategories
                                                  .length <= 1
                                              }
                                              color="error"
                                              onClick={() => {
                                                const cohots = [
                                                  ...trainingCohorts,
                                                ];

                                                const targetTrainees = [
                                                  ...cohots[cohortIndex]
                                                    .targetTrainees,
                                                ];

                                                const tmpTraineeCategories = [
                                                  ...traineeTarget.traineeCategories,
                                                ];

                                                tmpTraineeCategories.splice(
                                                  traineeCategoryIndex,
                                                  1,
                                                );

                                                targetTrainees[
                                                  traineeTargetIndex
                                                ] = {
                                                  ...traineeTarget,
                                                  traineeCategories:
                                                    tmpTraineeCategories,
                                                };

                                                cohots[cohortIndex] = {
                                                  ...cohort,
                                                  targetTrainees:
                                                    targetTrainees,
                                                };

                                                setTrainingCohorts(cohots);
                                              }}
                                            >
                                              <Tooltip
                                                title="Remove material"
                                                placement="top"
                                              >
                                                <span className="material-icons">
                                                  close
                                                </span>
                                              </Tooltip>
                                            </IconButton>
                                          </div>
                                        </fieldset>
                                      ),
                                    )}
                                  </fieldset>
                                </div>
                              </fieldset>
                                    );
                                  },
                                )}
                                {ttTotal >
                                  TARGET_TRAINEE_LOCATIONS_PAGE_SIZE && (
                                  <div className="d-flex justify-content-center flex-wrap align-items-center py-2">
                                    <ReactPaginate
                                      breakLabel="..."
                                      nextLabel={
                                        <>
                                          <span className="pr-1 d-none d-md-inline">
                                            Next
                                          </span>
                                          <i className="fas fa-angle-double-right"></i>
                                        </>
                                      }
                                      previousLabel={
                                        <>
                                          <i className="fas fa-angle-double-left"></i>
                                          <span className="pl-1 d-none d-md-inline">
                                            Previous
                                          </span>
                                        </>
                                      }
                                      pageRangeDisplayed={2}
                                      marginPagesDisplayed={1}
                                      onPageChange={({ selected }) => {
                                        setTargetTraineeLocationPageByCohortIdx(
                                          (prev) => ({
                                            ...prev,
                                            [cohortIndex]: selected,
                                          }),
                                        );
                                      }}
                                      pageCount={ttPageCount}
                                      forcePage={Math.min(
                                        ttPage,
                                        ttPageCount - 1,
                                      )}
                                      renderOnZeroPageCount={null}
                                      containerClassName="pagination mb-0"
                                      pageLinkClassName="btn btn-outline-dark btn-sm mr-1 ml-1"
                                      previousLinkClassName="btn btn-outline-dark btn-sm mr-1 ml-1"
                                      nextLinkClassName="btn btn-outline-dark btn-sm ml-1"
                                      activeLinkClassName="active"
                                    />
                                    <Typography
                                      variant="caption"
                                      component="span"
                                      className="ml-3 text-muted"
                                    >
                                      {`Showing ${ttSliceStart + 1}–${Math.min(
                                        ttSliceStart +
                                          TARGET_TRAINEE_LOCATIONS_PAGE_SIZE,
                                        ttTotal,
                                      )} of ${ttTotal}`}
                                    </Typography>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </fieldset>
                      </div>

                      <div className="col-12 px-3">
                        <fieldset
                          className={`px-3 mx-0 form-group border rounded elevated bg-white`}
                        >
                          <legend
                            className={` w-auto text-primary`}
                            style={{
                              fontFamily:
                                '"Roboto","Helvetica","Arial",sans-serif',
                              fontWeight: 400,
                              fontSize: "0.82rem",
                              lineHeight: "1em",
                              letterSpacing: "0.00938em",
                            }}
                          >
                            [{cohort.venues.length}] Venue
                            {cohort.venues.length > 1 ? "s" : ""}
                            <IconButton
                              color="primary"
                              onClick={() => {
                                const cohots = [...trainingCohorts];

                                const cohotVenues = [
                                  ...cohots[cohortIndex].venues,
                                ];

                                cohotVenues.push({
                                  ...trainingCohortDefaults.venues[0],
                                });

                                cohots[cohortIndex] = {
                                  ...cohort,
                                  venues: cohotVenues,
                                };

                                setTrainingCohorts(cohots);
                              }}
                            >
                              <Tooltip title="Add venue" placement="top">
                                <span className="material-icons">add</span>
                              </Tooltip>
                            </IconButton>
                          </legend>

                          {cohort.venues.map((cohortVenue, venueIndex) => (
                            <fieldset
                              key={venueIndex}
                              className={` mb-3 border rounded elevated px-3`}
                            >
                              <legend
                                className={`w-auto text-dark`}
                                style={{
                                  fontFamily:
                                    '"Roboto","Helvetica","Arial",sans-serif',
                                  fontWeight: 400,
                                  fontSize: "0.8rem",
                                  lineHeight: "1em",
                                  letterSpacing: "0.00938em",
                                }}
                              >
                                Venue #{venueIndex + 1}
                              </legend>

                              <div className="row">
                                <div className="col-12">
                                  <IconButton
                                    disabled={cohort.venues.length <= 1}
                                    className="float-right mt-n3 mr-n2"
                                    color="error"
                                    onClick={() => {
                                      const cohots = [...trainingCohorts];

                                      const cohotVenues = [
                                        ...cohots[cohortIndex].venues,
                                      ];

                                      cohotVenues.splice(venueIndex, 1);

                                      cohots[cohortIndex] = {
                                        ...cohort,
                                        venues: cohotVenues,
                                      };

                                      setTrainingCohorts(cohots);
                                    }}
                                  >
                                    <Tooltip
                                      title="Remove venue"
                                      placement="top"
                                    >
                                      <span className="material-icons">
                                        remove_circle_outline
                                      </span>
                                    </Tooltip>
                                  </IconButton>
                                </div>
                                <div className="col-2">
                                  <Autocomplete
                                    className="mb-3"
                                    size="small"
                                    defaultValue={null}
                                    value={cohortVenue.venueType}
                                    options={[
                                      ...venueTypes,
                                      { id: null, name: "Other" },
                                    ]}
                                    isOptionEqualToValue={(option, value) =>
                                      option.id === value.id
                                    }
                                    getOptionLabel={(option) => option.name}
                                    renderOption={(props, option) => (
                                      <Box component="li" {...props}>
                                        {option.name}
                                      </Box>
                                    )}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        name="venueType"
                                        label="Venue type"
                                        placeholder="Venue type"
                                        inputProps={{
                                          ...params.inputProps,
                                        }}
                                      />
                                    )}
                                    onChange={(event, venueType) => {
                                      const cohots = [...trainingCohorts];

                                      const cohotVenues = [
                                        ...cohots[cohortIndex].venues,
                                      ];

                                      cohotVenues[venueIndex] = {
                                        ...cohortVenue,
                                        venueType: venueType,
                                        venue: null,
                                        numOfTrainees: "",
                                      };

                                      cohots[cohortIndex] = {
                                        ...cohort,
                                        venues: cohotVenues,
                                      };

                                      setTrainingCohorts(cohots);
                                    }}
                                  />
                                </div>
                                <div className="col-2">
                                  <Autocomplete
                                    className="mb-3"
                                    size="small"
                                    defaultValue={null}
                                    value={cohortVenue.district}
                                    options={districts}
                                    isOptionEqualToValue={(option, value) =>
                                      option.id === value.id
                                    }
                                    getOptionLabel={(option) => option.name}
                                    renderOption={(props, option) => (
                                      <Box component="li" {...props}>
                                        {option.name}
                                      </Box>
                                    )}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        error={!!cohortVenue.errors?.district}
                                        helperText={
                                          cohortVenue.errors?.district || ""
                                        }
                                        name="district"
                                        label="Location district"
                                        placeholder="Location district"
                                        inputProps={{
                                          ...params.inputProps,
                                        }}
                                      />
                                    )}
                                    onChange={(event, district) => {
                                      const cohots = [...trainingCohorts];

                                      const cohotVenues = [
                                        ...cohots[cohortIndex].venues,
                                      ];

                                      getApiData(
                                        "/api/posts/sectors",
                                        district?.id,
                                        (data) => {
                                          cohotVenues[venueIndex] = {
                                            ...cohortVenue,
                                            district,
                                            sectors: data,
                                            sector: null,
                                            venues: [],
                                            venue: null,
                                            numOfTrainees: "",

                                            errors: {
                                              ...cohortVenue.errors,
                                              district: undefined,
                                            },
                                          };

                                          cohots[cohortIndex] = {
                                            ...cohort,
                                            venues: cohotVenues,
                                          };

                                          setTrainingCohorts(cohots);
                                        },
                                      );
                                    }}
                                  />
                                </div>

                                <div className="col-2">
                                  <Autocomplete
                                    className="mb-3"
                                    size="small"
                                    defaultValue={null}
                                    value={cohortVenue.sector}
                                    options={cohortVenue.sectors}
                                    isOptionEqualToValue={(option, value) =>
                                      option.id === value.id
                                    }
                                    getOptionLabel={(option) => option.name}
                                    renderOption={(props, option) => (
                                      <Box component="li" {...props}>
                                        {option.name}
                                      </Box>
                                    )}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        error={!!cohortVenue.errors?.sector}
                                        helperText={
                                          cohortVenue.errors?.sector || ""
                                        }
                                        name="sector"
                                        label="Location sector"
                                        placeholder="Location sector"
                                        inputProps={{
                                          ...params.inputProps,
                                        }}
                                      />
                                    )}
                                    onChange={(event, sector) => {
                                      const cohots = [...trainingCohorts];

                                      const cohotVenues = [
                                        ...cohots[cohortIndex].venues,
                                      ];

                                      cohotVenues[venueIndex] = {
                                        ...cohortVenue,
                                        sector,
                                      };

                                      getApiData(
                                        "/api/cpd/venues",
                                        sector?.id,
                                        (data) => {
                                          cohotVenues[venueIndex] = {
                                            ...cohortVenue,
                                            sector: sector,
                                            venues: data,
                                            venue: null,
                                            numOfTrainees: "",

                                            errors: {
                                              ...cohortVenue.errors,
                                              sector: undefined,
                                            },
                                          };

                                          cohots[cohortIndex] = {
                                            ...cohort,
                                            venues: cohotVenues,
                                          };

                                          setTrainingCohorts(cohots);
                                        },
                                      );
                                    }}
                                  />
                                </div>

                                <div className="col-4">
                                  <Autocomplete
                                    className="mb-3"
                                    size="small"
                                    defaultValue={null}
                                    value={cohortVenue.venue}
                                    options={cohortVenue.venues?.filter(
                                      ({ venueTypeId }) =>
                                        venueTypeId ===
                                        (cohortVenue.venueType?.id || null),
                                    )}
                                    isOptionEqualToValue={(option, value) =>
                                      option.id === value.id
                                    }
                                    getOptionLabel={(option) => option.name}
                                    renderOption={(props, option) => (
                                      <Box component="li" {...props}>
                                        {option.name}
                                      </Box>
                                    )}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        error={!!cohortVenue.errors?.venue}
                                        helperText={
                                          cohortVenue.errors?.venue || ""
                                        }
                                        name="venue"
                                        label="Venue"
                                        placeholder="Venue"
                                        inputProps={{
                                          ...params.inputProps,
                                        }}
                                      />
                                    )}
                                    onChange={(event, venue) => {
                                      const cohots = [...trainingCohorts];

                                      const cohotVenues = [
                                        ...cohots[cohortIndex].venues,
                                      ];

                                      cohotVenues[venueIndex] = {
                                        ...cohortVenue,
                                        venue,

                                        numOfTrainees: "",

                                        errors: {
                                          ...cohortVenue.errors,
                                          venue: undefined,
                                        },
                                      };

                                      cohots[cohortIndex] = {
                                        ...cohort,
                                        venues: cohotVenues,
                                      };

                                      setTrainingCohorts(cohots);
                                    }}
                                  />
                                </div>
                                <div className="col-2">
                                  <TextField
                                    error={!!cohortVenue.errors?.numOfTrainees}
                                    helperText={
                                      cohortVenue.errors?.numOfTrainees || ""
                                    }
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    type="number"
                                    name="venue-limit"
                                    label="Number of Trainees"
                                    placeholder="Number of Trainees"
                                    value={cohortVenue.numOfTrainees || 0}
                                    onChange={(e) => {
                                      const cohots = [...trainingCohorts];

                                      const cohotVenues = [
                                        ...cohots[cohortIndex].venues,
                                      ];

                                      let value = e.target.value || "";

                                      if (value.length > 1) {
                                        value = value.replace(/^0+/, "");
                                      }

                                      cohotVenues[venueIndex] = {
                                        ...cohortVenue,
                                        numOfTrainees: value,

                                        errors: {
                                          ...cohortVenue.errors,
                                          numOfTrainees: undefined,
                                        },
                                      };

                                      cohots[cohortIndex] = {
                                        ...cohort,
                                        venues: cohotVenues,
                                      };

                                      setTrainingCohorts(cohots);
                                    }}
                                  />
                                </div>
                              </div>
                              <fieldset className={`px-3  form-group`}>
                                <legend
                                  className={` w-auto text-primary`}
                                  style={{
                                    fontFamily:
                                      '"Roboto","Helvetica","Arial",sans-serif',
                                    fontWeight: 400,
                                    fontSize: "0.82rem",
                                    lineHeight: "1em",
                                    letterSpacing: "0.00938em",
                                  }}
                                >
                                  Trainers
                                </legend>

                                <div className="row mt-2">
                                  <div className="col-11">
                                    <TextField
                                      error={!!cohortVenue.errors?.trainers}
                                      helperText={
                                        cohortVenue.errors?.trainers || ""
                                      }
                                      variant="outlined"
                                      size="small"
                                      className="mb-3"
                                      type="text"
                                      fullWidth
                                      name={`trainerSearchTerm-${venueIndex}`}
                                      label={`Search by NID, Phone , or Email`}
                                      placeholder={`Search by NID, Phone , or Email`}
                                      value={cohortVenue.trainerSearchTerm}
                                      onChange={(e) => {
                                        const cohots = [...trainingCohorts];

                                        const cohotVenues = [
                                          ...cohots[cohortIndex].venues,
                                        ];

                                        cohotVenues[venueIndex] = {
                                          ...cohortVenue,
                                          trainerSearchTerm:
                                            e.target.value || "",
                                        };

                                        cohots[cohortIndex] = {
                                          ...cohort,
                                          venues: cohotVenues,
                                        };

                                        setTrainingCohorts(cohots);
                                      }}
                                    />
                                  </div>

                                  <div className="col-1 text-right">
                                    <Button
                                      variant="contained"
                                      disabled={
                                        !cohortVenue.trainerSearchTerm ||
                                        !!isLoading
                                      }
                                      onClick={async () => {
                                        const data = await searchTrainer(
                                          cohortVenue.trainerSearchTerm,
                                        );

                                        const cohots = [...trainingCohorts];

                                        const cohotVenues = [
                                          ...cohots[cohortIndex].venues,
                                        ];

                                        const trainers = [
                                          ...cohotVenues[venueIndex].trainers,
                                        ];

                                        if (
                                          !!data &&
                                          !trainers.find(
                                            ({ id }) => id === data.id,
                                          )
                                        )
                                          trainers.push({
                                            ...data,
                                            isVenueCoordinator: false,
                                          });

                                        cohotVenues[venueIndex] = {
                                          ...cohortVenue,

                                          trainers,
                                          trainerSearchTerm: "",
                                          errors: {
                                            ...cohortVenue.errors,
                                            trainers: undefined,
                                          },
                                        };

                                        cohots[cohortIndex] = {
                                          ...cohort,
                                          venues: cohotVenues,
                                        };

                                        setTrainingCohorts(cohots);
                                      }}
                                    >
                                      Add
                                    </Button>
                                  </div>
                                </div>

                                {!!cohortVenue?.trainers?.length && (
                                  <table className="table table-striped">
                                    <thead>
                                      <tr
                                        style={{ backgroundColor: "#f0f2f5" }}
                                      >
                                        <th scope="col">#</th>
                                        <th scope="col">RegNo</th>
                                        <th scope="col">Names</th>
                                        <th scope="col">NID Number</th>
                                        <th scope="col">Email</th>
                                        <th scope="col">Phone</th>
                                        <th scope="col"></th>
                                      </tr>
                                    </thead>

                                    <tbody>
                                      {cohortVenue?.trainers?.map(
                                        (item, trainerIndex) => (
                                          <tr key={item.id}>
                                            <td className="text-left">
                                              {trainerIndex + 1}
                                            </td>
                                            <td className="text-left">
                                              <strong>{item.id}</strong>
                                            </td>
                                            <td className="text-left">
                                              {item.lastName} {item.firstName}
                                            </td>
                                            <td className="text-left">
                                              {item.nidNumber}
                                            </td>
                                            <td className="text-left">
                                              {item.email}
                                            </td>
                                            <td className="text-left">
                                              {item.phoneNumber}
                                            </td>

                                            <td className="text-center">
                                              <button
                                                onClick={() => {
                                                  const cohots = [
                                                    ...trainingCohorts,
                                                  ];

                                                  const cohotVenues = [
                                                    ...cohots[cohortIndex]
                                                      .venues,
                                                  ];

                                                  const trainers = [
                                                    ...cohotVenues[venueIndex]
                                                      .trainers,
                                                  ];

                                                  trainers.splice(
                                                    trainerIndex,
                                                    1,
                                                  );

                                                  cohotVenues[venueIndex] = {
                                                    ...cohortVenue,
                                                    trainers,
                                                  };

                                                  cohots[cohortIndex] = {
                                                    ...cohort,
                                                    venues: cohotVenues,
                                                  };

                                                  setTrainingCohorts(cohots);
                                                }}
                                                className="btn btn-link btn-sm text-truncate text-danger"
                                              >
                                                Remove
                                              </button>
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </table>
                                )}
                                {!cohortVenue?.trainers?.length && (
                                  <div className="container text-center jumbotron py-3">
                                    <p className="lead">Trainers not set</p>
                                  </div>
                                )}
                              </fieldset>
                            </fieldset>
                          ))}
                        </fieldset>
                      </div>
                    </fieldset>
                  ))}
                </fieldset>

                <fieldset
                  className={`px-3 mt-3 no-gutters form-group border bg-light rounded elevated`}
                >
                  <legend
                    className={` w-auto text-primary`}
                    style={{
                      fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                      fontWeight: 400,
                      fontSize: "0.82rem",
                      lineHeight: "1em",
                      letterSpacing: "0.00938em",
                    }}
                  >
                    [{trainingMaterials.length}] Training material
                    {trainingMaterials.length > 1 ? "s" : ""}
                    <IconButton
                      color="primary"
                      onClick={() => {
                        const materials = [...trainingMaterials];
                        materials.push(trainingMaterialDefaults);
                        setTrainingMaterials(materials);
                      }}
                    >
                      <Tooltip title="Add material" placement="top">
                        <span className="material-icons">add</span>
                      </Tooltip>
                    </IconButton>
                  </legend>

                  {trainingMaterials.map((material, index) => (
                    <div className="row no-gutters" key={index}>
                      <div className="col-5">
                        <TextField
                          error={!!material.errors?.description}
                          helperText={material.errors?.description || ""}
                          size="small"
                          className="mb-3"
                          required
                          type="text"
                          fullWidth
                          name="description"
                          label="Description"
                          placeholder="Description"
                          variant="outlined"
                          value={material.description}
                          onChange={(event) => {
                            const materials = [...trainingMaterials];

                            materials[index] = {
                              ...material,
                              description: event.target.value,

                              errors: {
                                ...material.errors,
                                description: undefined,
                              },
                            };
                            setTrainingMaterials(materials);
                          }}
                        />
                      </div>
                      <div className="col-6 pl-3">
                        <TextField
                          error={!!material.errors?.url}
                          helperText={material.errors?.url || ""}
                          size="small"
                          className="mb-3"
                          required
                          type="url"
                          fullWidth
                          name="url"
                          label="Material's URL"
                          placeholder="Material's URL"
                          variant="outlined"
                          value={material.url}
                          onChange={(event) => {
                            const materials = [...trainingMaterials];

                            materials[index] = {
                              ...material,
                              url: event.target.value,

                              errors: {
                                ...material.errors,
                                url: undefined,
                              },
                            };
                            setTrainingMaterials(materials);
                          }}
                        />
                      </div>
                      <div className="col-1 text-center">
                        <IconButton
                          disabled={trainingMaterials.length <= 1}
                          color="error"
                          onClick={() => {
                            const materials = [...trainingMaterials];
                            materials.splice(index, 1);
                            setTrainingMaterials(materials);
                          }}
                        >
                          <Tooltip title="Remove material" placement="top">
                            <span className="material-icons">close</span>
                          </Tooltip>
                        </IconButton>
                      </div>
                    </div>
                  ))}
                </fieldset>

                <fieldset
                  className={`px-3 mt-3 no-gutters form-group border bg-light rounded elevated`}
                >
                  <legend
                    className={` w-auto text-primary`}
                    style={{
                      fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                      fontWeight: 400,
                      fontSize: "0.82rem",
                      lineHeight: "1em",
                      letterSpacing: "0.00938em",
                    }}
                  >
                    Needs assessment
                  </legend>

                  <div className="row no-gutters">
                    <div className="col-12 mt-3">
                      <TextField
                        error={!!errors?.descriptionError}
                        helperText={errors?.descriptionError || ""}
                        multiline
                        rows={3}
                        fullWidth
                        name="description"
                        label="Describe your needs assessment"
                        placeholder="Describe your needs assessment"
                        value={needsAssessment.description}
                        onChange={(e) => {
                          setNeedsAssessment({
                            ...needsAssessment,
                            description: e.target.value || "",
                          });
                          setErrors({ ...errors, descriptionError: "" });
                        }}
                      />
                    </div>
                    <div
                      className={`col-12 mb-3 ${!!errors.fileError ? "text-danger" : ""
                        } `}
                    >
                      <small>Upload needs assessment document</small>
                      {!!persistedNeedsAssessmentPath &&
                        !needsAssessment.file && (
                          <small className="d-block text-muted mt-1">
                            A PDF is already stored with this draft. You can
                            submit as-is or choose a new file to replace it.
                          </small>
                        )}
                      <Button
                        fullWidth
                        className="d-flex justify-content-start"
                        component="label"
                        style={{
                          borderRadius: "0px",
                          height: "35px",
                          border: `1px solid #6c757d`,
                          color: "#fff",
                          alignItems: "center",
                          fontSize: ".75rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          textTransform: "initial",
                          backgroundColor: "#6c757d",
                          "&:hover": {
                            backgroundColor: "#6c757d",
                          },
                        }}
                        size="md"
                      >
                        <input
                          accept="application/pdf"
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files[0];

                            setNeedsAssessment({
                              ...needsAssessment,
                              file: file || null,
                            });

                            setErrors({ ...errors, fileError: "" });
                          }}
                        />
                      </Button>
                      {!!errors.fileError && <small>{errors.fileError}</small>}
                    </div>
                  </div>
                </fieldset>
              </fieldset>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions className="d-flex justify-content-center gap-2 flex-wrap">
        <Button
          variant="outlined"
          color="primary"
          onClick={onSaveDraft}
          type="button"
          className="px-4"
          disabled={loading || isLoading}
        >
          Save as draft
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onSave}
          type="button"
          className="px-5"
          disabled={loading || isLoading}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = ({
  user,
  loading,
  districts,
  providerPrograms,
  academicYears,
  traineeCategories,
  schoolSubLevels,
  schoolSubLevelSubjects,
  venueTypes,
}) => {
  return {
    user,
    loading,
    districts,
    providerPrograms,
    academicYears,
    traineeCategories,
    schoolSubLevels,
    schoolSubLevelSubjects,
    venueTypes,
  };
};

export default connect(mapStateToProps, {
  saveTraining,
  getProviderPrograms,
  getTraineeSubCategories,
  getSchoolSubLevelSubjects,
})(TrainingFormDialog);
